import { useState } from "react";
import { ShieldAlert } from "lucide-react";

interface Props {
  host: string;
  command: string;
  reason: string;
  /** Resolve the held egress `ask`. `note` is sent only with a deny. */
  onDecide: (decision: "approved" | "denied", note: string) => Promise<void>;
}

// Inline approve/deny card for a held PTY egress `ask`. The agent's y/n prompt
// is held on this terminal, so the card renders ON the terminal — the user is
// already looking at the held prompt. Reuses the `.approval-card` styling so it
// matches the chat ApprovalCard. Without this card an `ask`+enforce terminal
// would hang with no way to resolve it (the P1 this closes).
export function EgressApprovalCard({ host, command, reason, onDecide }: Props) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: "approved" | "denied") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDecide(decision, note.trim());
      // On success the card unmounts (the resolved WS message clears state); no
      // need to reset `busy`.
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not submit the decision.");
      setBusy(false);
    }
  }

  return (
    <div
      className="approval-card egress-approval-card"
      role="alertdialog"
      aria-label="Egress approval required"
    >
      <header className="approval-card__header">
        <ShieldAlert size={14} />
        <span>This terminal wants to reach</span>
        <code className="approval-card__command">{host}</code>
      </header>
      <div className="approval-card__meta">
        Pack: <strong>egress-allowlist</strong> · not on the allow-list
      </div>
      {command ? (
        <p className="approval-card__reason">
          <code>{command}</code>
        </p>
      ) : null}
      <p className="approval-card__reason">{reason}</p>
      <input
        className="egress-approval-card__note"
        type="text"
        value={note}
        disabled={busy}
        placeholder="Reason (sent if you deny)"
        aria-label="Reason for denying"
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void decide("denied");
          }
        }}
      />
      {error ? <p className="approval-card__reason approval-card__error">{error}</p> : null}
      <div className="approval-card__actions">
        <button type="button" disabled={busy} onClick={() => void decide("approved")}>
          Allow once
        </button>
        <button
          type="button"
          disabled={busy}
          className="danger"
          onClick={() => void decide("denied")}
        >
          Deny
        </button>
      </div>
    </div>
  );
}
